import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { TREATMENTS, CATEGORIES } from "../data/treatments";
import { Reveal, Eyebrow } from "../components/Reveal";

export default function TreatmentDetail() {
  const { t, link, lang, treatment } = useLocale();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);

  if (!treatment) return null;
  const related = TREATMENTS.filter((tr) => tr.key !== treatment.key && !tr.noPage)
    .sort((a, b) => (a.category === treatment.category ? -1 : 0) - (b.category === treatment.category ? -1 : 0))
    .slice(0, 3);

  return (
    <div data-testid={`treatment-detail-${treatment.key}`}>
      <section ref={ref} className="relative flex min-h-[60vh] items-end overflow-hidden pt-20">
        <motion.img src={treatment.img} alt={treatment.names[lang]} style={{ y }} className="absolute inset-x-0 top-20 h-[115%] w-full object-cover" />
        <div className="absolute inset-x-0 top-20 bottom-0 bg-gradient-to-t from-imperium-brown/70 via-imperium-brown/20 to-transparent" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1600px] px-6 pb-14 sm:px-10 lg:px-16">
          <Reveal>
            <Eyebrow className="text-imperium-champagne">{CATEGORIES.find((c) => c.key === treatment.category)?.names[lang]}</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-serif text-4xl font-light leading-tight text-imperium-white sm:text-6xl" data-testid="treatment-title">
              {treatment.names[lang]}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 py-20 sm:px-10 lg:px-16 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-serif text-2xl font-light leading-relaxed text-imperium-black sm:text-3xl">{treatment.short[lang]}</p>
              <div className="my-10 h-px w-24 bg-imperium-gold" aria-hidden="true" />
              <p className="max-w-xl text-base font-light leading-relaxed text-imperium-black/70" data-testid="treatment-description">{treatment.desc[lang]}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <div className="border border-imperium-champagne/50 bg-imperium-offwhite p-8 lg:p-10">
                <h2 className="font-serif text-2xl font-light text-imperium-black">{t("treatment.ctaTitle")}</h2>
                <p className="mt-4 text-sm font-light leading-relaxed text-imperium-black/65">{t("treatment.ctaText")}</p>
                <Link
                  to={link("contact")}
                  data-testid="treatment-cta-book"
                  className="mt-8 inline-block border border-imperium-bronze px-7 py-3.5 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors duration-300 hover:bg-imperium-brown hover:text-imperium-white"
                >
                  {t("cta.request")}
                </Link>
                <a href="https://wa.me/41786136096" target="_blank" rel="noopener noreferrer" data-testid="treatment-cta-whatsapp" className="link-line mt-6 inline-block text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown/80">
                  {t("cta.whatsapp")}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 pb-24 sm:px-10 lg:px-16 lg:pb-32">
        <Reveal>
          <div className="mb-10 flex items-center gap-6">
            <h2 className="text-[11px] font-label uppercase tracking-[0.35em] text-imperium-bronze">{t("treatment.related")}</h2>
            <div className="h-px flex-1 bg-imperium-champagne/50" aria-hidden="true" />
          </div>
        </Reveal>
        {related.map((tr, i) => (
          <Reveal key={tr.key} delay={0.05 * i}>
            <Link
              to={link("treatments", tr.slugs[lang])}
              data-testid={`related-treatment-${tr.key}`}
              className="group flex items-center justify-between border-t border-imperium-champagne/40 py-6 transition-all duration-500 last:border-b hover:pl-4"
            >
              <span className="font-serif text-2xl font-light text-imperium-black transition-colors group-hover:text-imperium-bronze sm:text-3xl">{tr.names[lang]}</span>
              <ArrowUpRight className="h-5 w-5 text-imperium-bronze transition-transform duration-500 group-hover:rotate-45" />
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.1}>
          <Link to={link("treatments")} data-testid="treatment-back-link" className="group mt-12 inline-flex items-center gap-3 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown">
            <ArrowLeft className="h-4 w-4 transition-transform duration-500 group-hover:-translate-x-1" />
            <span className="link-line">{t("treatment.back")}</span>
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
